/**
 * Aus einem Grundriss wird ein Gebaeude.
 *
 * Diese Datei erzeugt reine Geometrie ohne Texturen und ohne Browser. Deshalb kann
 * ein Test in Node nachrechnen, dass die Grundflaeche eines Gebaeudes exakt seinem
 * Bauplatz entspricht - genau das verlangt Abnahmekriterium A. Die Materialien kommen
 * erst eine Ebene darueber dazu.
 *
 * Regel fuer jeden Stil: das **unterste** Bauteil ist immer so breit und so tief wie
 * der Grundriss, alles darueber ist gleich gross oder kleiner. Damit ist die
 * Grundflaeche zugleich die Huellflaeche.
 */
import type * as THREE from 'three'
import type { HotelId } from '../core/data/hotels'
import type { Grundriss } from '../core/geometrie'
import { kuppel, quader, spitze, tonnendach, verbinde, walmdach } from './formen'
import { EINLASS } from './ebenen'

export type Bauform =
  | 'turm'
  | 'palast'
  | 'block'
  | 'pagode'
  | 'kuppelbau'
  | 'bungalow'
  | 'lodge'
  | 'welle'

export interface Baustil {
  readonly form: Bauform
  readonly wand: string
  readonly fenster: string
  readonly dach: string
  readonly dachDunkel: string
  /** Hoehe je Ausbaustufe, in derselben Reihenfolge wie die Besitzkarte. */
  readonly hoehen: readonly number[]
  /** Farben des Freizeitparks: Rasen hell, Rasen dunkel, Wasser. */
  readonly park: readonly [string, string, string]
  /** Anteil der Bauhoehe, der Mauerwerk ist - Grundlage fuer die Fensterreihen. */
  readonly koerperAnteil: number
}

export const BAUSTILE: Readonly<Record<HotelId, Baustil>> = {
  kapitol: {
    form: 'turm',
    wand: '#93b6dd',
    fenster: '#26456f',
    dach: '#39485c',
    dachDunkel: '#2a3648',
    hoehen: [78, 62, 50, 42],
    park: ['#4f9c5f', '#3d7c4b', '#3f8fc4'],
    koerperAnteil: 0.93,
  },
  belvedere: {
    form: 'palast',
    wand: '#f2ece0',
    fenster: '#56687f',
    dach: '#6b6f78',
    dachDunkel: '#51555d',
    hoehen: [44, 38, 34, 32, 30],
    park: ['#5aa36a', '#437f52', '#4a9ed0'],
    koerperAnteil: 0.68,
  },
  regent: {
    form: 'block',
    wand: '#e9dccb',
    fenster: '#31445c',
    dach: '#3f7d5f',
    dachDunkel: '#2f5f48',
    hoehen: [46, 40, 35, 32],
    park: ['#57a066', '#3f7a4e', '#4a9ed0'],
    koerperAnteil: 0.84,
  },
  lagune: {
    form: 'bungalow',
    wand: '#f7e6c8',
    fenster: '#3d5a54',
    dach: '#bb7a41',
    dachDunkel: '#8d5a2f',
    hoehen: [23, 21, 20, 19, 18],
    park: ['#63b072', '#4a8a58', '#37b0d8'],
    koerperAnteil: 0.5,
  },
  pagode: {
    form: 'pagode',
    wand: '#efe6d8',
    fenster: '#3a4a55',
    dach: '#8f3b33',
    dachDunkel: '#6d2c26',
    hoehen: [40, 34, 30],
    park: ['#5aa36a', '#41794e', '#4a9ed0'],
    koerperAnteil: 0.28,
  },
  maharani: {
    form: 'kuppelbau',
    wand: '#f8f5ef',
    fenster: '#586a7a',
    dach: '#e6e0d3',
    dachDunkel: '#c9c2b2',
    hoehen: [38, 30, 26],
    park: ['#5aa36a', '#41794e', '#4a9ed0'],
    koerperAnteil: 0.58,
  },
  savanne: {
    form: 'lodge',
    wand: '#cba874',
    fenster: '#3b3126',
    dach: '#7a5e3c',
    dachDunkel: '#5b452c',
    hoehen: [25, 22, 20],
    park: ['#8ea55a', '#7d8a4a', '#4a9ed0'],
    koerperAnteil: 0.42,
  },
  koralle: {
    form: 'welle',
    wand: '#fdf6ee',
    fenster: '#3d6f86',
    dach: '#e07a3f',
    dachDunkel: '#b25c2b',
    hoehen: [34],
    park: ['#63b072', '#4a8a58', '#2fb6c8'],
    koerperAnteil: 0.58,
  },
}

/** Die Hoehe, die dieses Gebaeude bekommt. */
export function bauhoehe(hotel: HotelId, stufe: number): number {
  const h = BAUSTILE[hotel].hoehen
  return h[Math.min(stufe, h.length - 1)]!
}

export interface Bauteile {
  /** Mauerwerk: bekommt die Fassadentextur. */
  readonly koerper: THREE.BufferGeometry
  /** Dachflaechen: bekommen die Dachtextur. */
  readonly dach: THREE.BufferGeometry
}

/**
 * Erzeugt die Geometrie eines Gebaeudes im lokalen System des Bauplatzes:
 * Ursprung in der Mitte des Grundrisses, y = 0 auf Gelaendeoberkante.
 */
export function gebaeudeTeile(hotel: HotelId, stufe: number, riss: Grundriss): Bauteile {
  const stil = BAUSTILE[hotel]
  const b = riss.breite
  const t = riss.tiefe
  const H = bauhoehe(hotel, stufe)
  const koerper: THREE.BufferGeometry[] = []
  const dach: THREE.BufferGeometry[] = []
  const unten = -EINLASS

  switch (stil.form) {
    case 'turm':
      koerper.push(quader(b, t, unten, H * 0.08))
      koerper.push(quader(b * 0.9, t * 0.9, H * 0.08, H * 0.8))
      koerper.push(quader(b * 0.66, t * 0.66, H * 0.8, H * 0.93))
      dach.push(quader(b * 0.66, t * 0.66, H * 0.93, H * 0.96))
      dach.push(spitze(Math.min(b, t) * 0.06, H * 0.96, H * 0.04))
      break

    case 'palast':
      koerper.push(quader(b, t, unten, H * 0.68))
      dach.push(walmdach(b, t, H * 0.68, H * 0.24, 0.62))
      dach.push(quader(b * 0.16, t * 0.16, H * 0.86, H * 0.94))
      dach.push(spitze(Math.min(b, t) * 0.09, H * 0.94, H * 0.06))
      break

    case 'block':
      koerper.push(quader(b, t, unten, H * 0.78))
      koerper.push(quader(b, t, H * 0.78, H * 0.84))
      dach.push(quader(b * 0.9, t * 0.9, H * 0.84, H * 0.88))
      dach.push(walmdach(b * 0.9, t * 0.9, H * 0.88, H * 0.12, 0.7))
      break

    case 'pagode':
      // Drei Geschosse, jedes mit eigenem, steilem Zeltdach: erst die Steilheit
      // macht aus einem roten Kasten eine Pagode.
      koerper.push(quader(b, t, unten, H * 0.24))
      dach.push(walmdach(b, t, H * 0.24, H * 0.2, 0.18))
      koerper.push(quader(b * 0.68, t * 0.68, H * 0.44, H * 0.58))
      dach.push(walmdach(b * 0.82, t * 0.82, H * 0.58, H * 0.17, 0.16))
      koerper.push(quader(b * 0.4, t * 0.4, H * 0.75, H * 0.84))
      dach.push(walmdach(b * 0.54, t * 0.54, H * 0.84, H * 0.12, 0.14))
      dach.push(spitze(Math.min(b, t) * 0.045, H * 0.96, H * 0.04))
      break

    case 'kuppelbau': {
      const r = Math.min(b, t) * 0.34
      koerper.push(quader(b, t, unten, H * 0.58))
      koerper.push(quader(b * 0.62, t * 0.62, H * 0.58, H * 0.62))
      dach.push(kuppel(r, H * 0.62, H * 0.32))
      dach.push(spitze(r * 0.2, H * 0.94, H * 0.06))
      // Vier Ecktuermchen, deutlich innerhalb des Grundrisses.
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const turm = quader(b * 0.1, t * 0.1, unten, H * 0.74)
          turm.translate(sx * (b * 0.42), 0, sz * (t * 0.42))
          koerper.push(turm)
          const helm = spitze(Math.min(b, t) * 0.055, H * 0.74, H * 0.12)
          helm.translate(sx * (b * 0.42), 0, sz * (t * 0.42))
          dach.push(helm)
        }
      }
      break
    }

    case 'bungalow':
      koerper.push(quader(b, t, unten, H * 0.5))
      dach.push(walmdach(b, t, H * 0.5, H * 0.5, 0.4))
      break

    case 'lodge':
      koerper.push(quader(b, t, unten, H * 0.42))
      dach.push(walmdach(b, t, H * 0.42, H * 0.58, 0.22))
      break

    case 'welle':
      koerper.push(quader(b, t, unten, H * 0.58))
      dach.push(tonnendach(b, t, H * 0.58, H * 0.42))
      break
  }

  return { koerper: verbinde(koerper), dach: verbinde(dach) }
}

/** Die Bauteile des Freizeitparks: Rasenflaeche, Wasser, Baeume. */
export interface Parkteile {
  readonly rasen: THREE.BufferGeometry
  readonly wasser: THREE.BufferGeometry
  readonly baeume: THREE.BufferGeometry
  readonly staemme: THREE.BufferGeometry
}

export function parkTeile(riss: Grundriss): Parkteile {
  const b = riss.breite
  const t = riss.tiefe
  const rasen = quader(b, t, -EINLASS, 1.4)
  const wasser = quader(b * 0.42, t * 0.5, 1.4, 1.9)
  wasser.translate(-b * 0.22, 0, 0)

  const baeume: THREE.BufferGeometry[] = []
  const staemme: THREE.BufferGeometry[] = []
  const plaetze: readonly (readonly [number, number, number])[] = [
    [0.22, -0.26, 1],
    [0.34, 0.1, 0.85],
    [0.16, 0.3, 0.7],
    [-0.36, 0.34, 0.75],
  ]
  for (const [fx, fz, gr] of plaetze) {
    const r = Math.min(b, t) * 0.1 * gr
    const stamm = quader(r * 0.4, r * 0.4, 1.4, 1.4 + r * 1.6)
    stamm.translate(b * fx, 0, t * fz)
    staemme.push(stamm)
    const krone = kuppel(r, 1.4 + r * 1.6, r * 1.7)
    krone.translate(b * fx, 0, t * fz)
    baeume.push(krone)
  }
  return {
    rasen,
    wasser,
    baeume: verbinde(baeume),
    staemme: verbinde(staemme),
  }
}
